import { CheckCircle2, Clock } from 'lucide-react'
import { Link } from 'react-router'
import { trpc } from '@/config/trpc'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { type PlatformType } from '@backend/lib/db/schema/platform'

interface Props {
  workspaceId?: number
  open: boolean
  /** Only this platform's posts are shown — the strip follows the modal's
   *  platform toggle, like the preview itself. */
  platform: PlatformType
}

/**
 * Compact status rows in the preview modal for the selected platform: what
 * this workspace has already committed there. Complements the server-side
 * publish/schedule locks — a platform shown here will be refused on another
 * attempt, so the user learns the state before hitting the wall. (Drift
 * re-sync lives in the workspace header badge, not here.)
 */
export const WorkspacePreviewPostStatus = ({ workspaceId, open, platform }: Props) => {
  const { data } = trpc.workspace.postStatus.useQuery(
    { id: workspaceId ?? 0 },
    { enabled: workspaceId != null && open },
  )

  const published = data?.published.filter((p) => p.platform === platform) ?? []
  const scheduled = data?.scheduled.filter((s) => s.platform === platform) ?? []

  if (published.length === 0 && scheduled.length === 0) return null

  return (
    <div className="mx-auto w-full max-w-[450px]">
      <div className="border-border flex flex-col gap-1.5 rounded-lg border px-4 py-3 text-sm">
        {published.map((post) => (
          <div
            key={`p-${post.platform}-${post.accountId}`}
            className="text-foreground flex items-center gap-2">
            <CheckCircle2 size={16} className="flex-shrink-0 text-green-600 dark:text-green-400" />
            <span className="font-medium">{getPlatformDisplayName(post.platform)}</span>
            <span>Published</span>
            {post.permalink && (
              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline underline-offset-2">
                View post
              </a>
            )}
          </div>
        ))}
        {scheduled.map((post) => (
          <div key={`s-${post.id}`} className="text-foreground flex items-center gap-2">
            <Clock size={16} className="text-muted-foreground flex-shrink-0" />
            <span className="font-medium">{getPlatformDisplayName(post.platform)}</span>
            <span>
              Scheduled{' '}
              {new Date(post.scheduledAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
            <Link to="/posts" className="font-medium underline underline-offset-2">
              Manage
            </Link>
          </div>
        ))}
        {published.length > 0 && (
          <p className="text-muted-foreground">Duplicate the workspace to post here again.</p>
        )}
      </div>
    </div>
  )
}
